import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import FeedbackIcon from '@material-ui/icons/Feedback';
import CloseIcon from '@material-ui/icons/Close';
import { FeedbackForm } from './FeedbackForm';

const useStyles = makeStyles(theme => ({
  fab: {
    position: 'fixed',
    // Above page content, below the app's sidebar (appBar) and dialogs: the
    // sidebar expanding on hover covers the button like the rest of the page.
    // The button is therefore placed beside the sidebar, never under it — see
    // `insets`.
    zIndex: theme.zIndex.speedDial,
    // The button is draggable: keep the browser from turning a finger drag
    // into a page scroll, and hint that it can be moved.
    touchAction: 'none',
    cursor: 'grab',
    userSelect: 'none',
  },
  dragging: {
    cursor: 'grabbing',
    transition: 'none',
    // While it follows the pointer it may cross the sidebar; it must not
    // vanish under it half-way.
    zIndex: theme.zIndex.appBar + 1,
  },
  settled: {
    transition: theme.transitions.create(['left', 'right', 'bottom'], {
      duration: theme.transitions.duration.shorter,
    }),
  },
  label: {
    marginLeft: theme.spacing(1),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(1),
  },
}));

/**
 * Longest page URL we attach. The backend rejects anything over 2048 chars,
 * and wizard pages carry the whole form in the query string (the Day-2
 * template is opened with `?formData=<json>`), so when the full URL would
 * not fit we send just origin + path: still enough to know where the
 * developer was.
 */
const PAGE_URL_MAX = 2000;

function currentPageUrl(): string {
  if (typeof window === 'undefined') return '';
  const { href, origin, pathname } = window.location;
  return href.length <= PAGE_URL_MAX ? href : `${origin}${pathname}`;
}

/**
 * Where the button rests: pinned to the left or right edge, some distance
 * up from the bottom. Storing the side rather than an x coordinate keeps
 * the button on screen when the window is resized.
 */
export interface FabPosition {
  side: 'left' | 'right';
  bottom: number;
}

/** Per-browser memory of where the developer left the button. */
export const FAB_POSITION_STORAGE_KEY = 'feedback.fab.position';
/** Gap kept between the button and the viewport edge, in px. */
export const EDGE_MARGIN = 24;
/** Pointer travel below this is a click, above it a drag, in px. */
export const DRAG_THRESHOLD = 6;

const DEFAULT_POSITION: FabPosition = { side: 'right', bottom: EDGE_MARGIN };

function loadPosition(): FabPosition {
  try {
    const raw = window.localStorage.getItem(FAB_POSITION_STORAGE_KEY);
    if (!raw) return DEFAULT_POSITION;
    const parsed = JSON.parse(raw);
    if (
      (parsed?.side === 'left' || parsed?.side === 'right') &&
      Number.isFinite(parsed?.bottom)
    ) {
      return { side: parsed.side, bottom: parsed.bottom };
    }
  } catch {
    // Private mode, blocked storage, corrupt value: fall through.
  }
  return DEFAULT_POSITION;
}

function savePosition(position: FabPosition) {
  try {
    window.localStorage.setItem(
      FAB_POSITION_STORAGE_KEY,
      JSON.stringify(position),
    );
  } catch {
    // Storage unavailable: the button simply will not remember its place.
  }
}

/**
 * Screen area the app's own navigation covers, in px: the sidebar on the
 * left on desktop, the bottom bar on mobile. The sidebar sits above this
 * button, so an edge that ignored it would hide the button behind it.
 */
export interface FabInsets {
  left?: number;
  bottom?: number;
}

/** Keep `bottom` such that a button of `height` px stays fully visible. */
function clampBottom(bottom: number, height: number, inset = 0): number {
  const min = inset + EDGE_MARGIN;
  const max = Math.max(min, window.innerHeight - height - EDGE_MARGIN);
  return Math.min(Math.max(min, Math.round(bottom)), max);
}

/**
 * Floating "Feedback" button shown on every page. Opens the feedback form in
 * a dialog; the current page URL travels with the submission so the platform
 * team knows where the developer was.
 *
 * The button can be dragged anywhere and snaps to the nearest side edge, so
 * it never has to sit on top of a page's own buttons (the Create wizard's
 * Back/Next, for instance). The position is remembered per browser.
 *
 * The dialog keeps the form mounted while closed, so a draft survives an
 * accidental Escape / backdrop click, and a submission that is still in
 * flight can finish and report back.
 *
 * Mount once, inside the app's Root (next to the page content), passing the
 * space the sidebar takes as `insets` so the button rests beside it.
 */
export const FeedbackFab = ({ insets = {} }: { insets?: FabInsets }) => {
  const insetLeft = insets.left ?? 0;
  const insetBottom = insets.bottom ?? 0;
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [open, setOpen] = useState(false);
  const [pageUrl, setPageUrl] = useState('');

  const [position, setPosition] = useState<FabPosition>(loadPosition);
  // While dragging the button follows the pointer as left/top coordinates.
  const [dragPoint, setDragPoint] = useState<{ x: number; y: number }>();
  const fabRef = useRef<HTMLButtonElement>(null);
  const gesture = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    dragged: boolean;
  }>();
  // Set by a drag so the click the browser fires afterwards is ignored.
  const suppressClick = useRef(false);

  // A window that got shorter must not leave the button off screen.
  useEffect(() => {
    const onResize = () =>
      setPosition(prev => {
        const height = fabRef.current?.offsetHeight ?? 0;
        const bottom = clampBottom(prev.bottom, height);
        return bottom === prev.bottom ? prev : { ...prev, bottom };
      });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleOpen = () => {
    setPageUrl(currentPageUrl());
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    handleOpen();
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    // Only the primary button / a single touch starts a gesture.
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    gesture.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      dragged: false,
    };
    // Keep receiving moves even when the pointer leaves the button.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== event.pointerId) return;
    if (!g.dragged) {
      const travel = Math.hypot(
        event.clientX - g.startX,
        event.clientY - g.startY,
      );
      if (travel < DRAG_THRESHOLD) return;
      g.dragged = true;
    }
    setDragPoint({
      x: event.clientX - g.offsetX,
      y: event.clientY - g.offsetY,
    });
  };

  const endGesture = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const g = gesture.current;
      if (!g || g.pointerId !== event.pointerId) return;
      gesture.current = undefined;
      event.currentTarget.releasePointerCapture?.(event.pointerId);
      if (!g.dragged) return;

      // Snap to the nearest side edge of the content area (the part of the
      // screen the sidebar does not cover) and remember it.
      const rect = event.currentTarget.getBoundingClientRect();
      const centerX = event.clientX - g.offsetX + rect.width / 2;
      const top = event.clientY - g.offsetY;
      const next: FabPosition = {
        side: centerX < (insetLeft + window.innerWidth) / 2 ? 'left' : 'right',
        bottom: clampBottom(
          window.innerHeight - top - rect.height,
          rect.height,
          insetBottom,
        ),
      };
      setDragPoint(undefined);
      setPosition(next);
      savePosition(next);
      suppressClick.current = true;
    },
    [insetLeft, insetBottom],
  );

  // The insets are applied here rather than stored: they change with the
  // sidebar (pinned open, collapsed, mobile bottom bar) and the stored
  // position has to stay valid for all of them.
  const style: CSSProperties = dragPoint
    ? { left: dragPoint.x, top: dragPoint.y, right: 'auto', bottom: 'auto' }
    : {
        [position.side]:
          position.side === 'left' ? insetLeft + EDGE_MARGIN : EDGE_MARGIN,
        bottom: Math.max(position.bottom, insetBottom + EDGE_MARGIN),
      };

  return (
    <>
      <Tooltip
        title="Send feedback to the platform team (drag to move)"
        placement={position.side === 'left' ? 'right' : 'left'}
      >
        <Fab
          ref={fabRef}
          color="primary"
          variant="extended"
          size="large"
          className={`${classes.fab} ${
            dragPoint ? classes.dragging : classes.settled
          }`}
          style={style}
          onClick={handleClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          aria-label="Send feedback"
          data-testid="feedback-fab"
          data-side={position.side}
        >
          <FeedbackIcon />
          <span className={classes.label}>Feedback</span>
        </Fab>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        keepMounted
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        aria-labelledby="feedback-dialog-title"
      >
        <DialogTitle disableTypography className={classes.title}>
          <Typography variant="h6" id="feedback-dialog-title">
            Send feedback
          </Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="Close feedback dialog"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <FeedbackForm
            pageUrl={pageUrl}
            onCancel={handleClose}
            onSubmitted={handleClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
